import * as React from "react";
import LeadIcon from "../../../assets/svg/lead.svg";
import LeadSolidIcon from "../../../assets/svg/lead-solid.svg";
import MatterIcon from "../../../assets/svg/matter.svg";
import MatterSolidIcon from "../../../assets/svg/matter-solid.svg";
import ViewIcon from "../../../assets/svg/view.svg";
import CloudIcon from "../../../assets/svg/cloud.svg";
import AssignIcon from "../../../assets/svg/assign.svg";
import { Image, Link } from "@fluentui/react-components";
import { StaffMemberPresentational } from "../StaffMember/StaffMemberPresentational";
import { getMatterLink } from "../../services/link-service";
import { SecondarySubtleButton } from "../Primitives/Buttons";
import type { TMatter } from "../../models/matter-model";

export interface MatterInfoPresentationalProps {
  className?: string;
  allowAction?: boolean;
  isSelected?: boolean;
  fullAreaSelect?: boolean;
  useOldStyle?: boolean;
  matter?: TMatter | null | undefined;

  saveToMatter?: () => void;
  assignToMatter?: (matter: TMatter | null) => void;
}

const handleViewClick = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.stopPropagation();
};

export const MatterInfoPresentational: React.FC<MatterInfoPresentationalProps> = (props) => {
  if (props.useOldStyle) {
    return oldStyle(props);
  }

  return newStyle(props);
};

const oldStyle: React.FC<MatterInfoPresentationalProps> = (props) => {
  return (
    <div className="flex p-2.5 text-gray-700 border border-gray-400 bg-[#E1ECF3] rounded-md m-[5px_5px_20px_5px]">
      {props.matter && (
        <>
          <Image
            className="self-start pt-1"
            src={props.matter.isLead ? LeadIcon : MatterIcon}
            alt={props.matter.isLead ? "Lead" : "Matter"}
          />
          <div className="flex flex-col px-2.5 flex-grow min-w-0 overflow-x-hidden text-ellipsis whitespace-nowrap">
            <div className="overflow-x-hidden text-ellipsis whitespace-nowrap" title={props.matter.client}>
              {props.matter.client}
            </div>
            <div className="overflow-x-hidden text-ellipsis whitespace-nowrap">
              <Link
                onClick={(e) => {
                  e.stopPropagation();
                }}
                href={getMatterLink(props.matter.matterId)}
                target="_blank"
              >
                {props.matter.description}
              </Link>
            </div>
            <div className="flex flex-row gap-2.5 items-center">
              {props.matter.dateOpened?.toLocaleDateString()}
              {props.matter.personResponsible && (
                <StaffMemberPresentational staffMember={props.matter.personResponsible} initials={true} />
              )}
              {props.matter.personResponsible && props.matter.personAssisting && "/"}
              {props.matter.personAssisting && (
                <StaffMemberPresentational staffMember={props.matter.personAssisting} initials={true} />
              )}
            </div>
            <div>{props.matter.matterTypeName}</div>
          </div>
        </>
      )}
    </div>
  );
};

const newStyle: React.FC<MatterInfoPresentationalProps> = (props) => {
  const handleAssignToMatter = () => {
    (props.matter && props.assignToMatter?.(props.matter)) ??
      console.warn("handleAssignToMatter: matter or assignToMatter property is missing, action skipped.");
  };

  const handleSaveToMatter = () => {
    props.saveToMatter?.() ?? console.warn("handleSaveToMatter: saveToMatter property is missing, action skipped.");
  };

  return (
    <div className={`${props.className ?? ""} flex-1`} id={props.matter?.matterId}>
      {props.matter && (
        <>
          <div
            className={`flex flex-col px-2 flex-1 pt-2 pb-1 border-l-4 ${
              props.isSelected ? (props.matter.isLead ? "border-black" : "border-primary-orange-200") : ""
            }`}
          >
            <div className="matter-icon-part flex flex-row">
              <img
                src={props.matter.isLead ? LeadSolidIcon : MatterSolidIcon}
                alt={props.matter.isLead ? "Lead" : "Matter"}
                className="self-start w-8 h-8 mt-1"
              />

              <div className="ml-2 overflow-hidden">
                <span
                  className="font-semibold block overflow-hidden text-nowrap text-ellipsis"
                  title={props.matter.client}
                >
                  {props.matter.client}
                </span>
                <span className="block overflow-hidden text-ellipsis">{props.matter.matterTypeName}</span>
              </div>
            </div>

            <div className="matter-content-part flex flex-col mt-2 overflow-hidden">
              <span className="block overflow-hidden text-nowrap text-ellipsis">{props.matter.description}</span>
              <div className="flex flex-row gap-2 items-center justify-between overflow-hidden">
                <div className="flex items-center overflow-hidden text-nowrap mt-1">
                  <span className="overflow-hidden text-nowrap text-ellipsis">
                    Opened - {props.matter.dateOpened?.toLocaleDateString()}
                  </span>
                </div>
                <div className="staff-part ml-auto flex items-center text-nowrap overflow-hidden">
                  {props.matter.personResponsible && (
                    <StaffMemberPresentational staffMember={props.matter.personResponsible} initials={true} />
                  )}
                  {props.matter.personResponsible && props.matter.personAssisting ? (
                    <span className="w-1"></span>
                  ) : null}
                  {props.matter.personAssisting && (
                    <StaffMemberPresentational staffMember={props.matter.personAssisting} initials={true} />
                  )}
                </div>
              </div>
            </div>
            <div className="matter-action-part flex flex-row items-center justify-between border-t border-neutral-100 mt-3 pt-1">
              <div className="flex flex-1 justify-around">
                {!props.isSelected && (
                  <SecondarySubtleButton onClick={handleAssignToMatter} leftIcon={AssignIcon}>
                    Assign
                  </SecondarySubtleButton>
                )}
                <SecondarySubtleButton
                  onClick={handleViewClick}
                  href={getMatterLink(props.matter.matterId)}
                  target="_blank"
                  leftIcon={ViewIcon}
                >
                  View
                </SecondarySubtleButton>
                {props.isSelected && (
                  <SecondarySubtleButton onClick={handleSaveToMatter} leftIcon={CloudIcon}>
                    Save to Matter
                  </SecondarySubtleButton>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
